import { Editor } from '@tiptap/core'
import Element, { ElementType } from '@/modules/elements/models/Element'

import type { HeadlessFormatTarget } from '@/util/headless-format'

export interface ElementComponentRef {
  elementRef?: {
    getEditor?: () => Editor | null
    getFormatTarget?: () => Editor | HeadlessFormatTarget | null
    editor?: Editor | null
  } | null
}

export function getEditorFromElementRef(elementRef: ElementComponentRef['elementRef']): Editor | null {
  if (!elementRef) {
    return null
  }

  let editor: Editor | null = null
  if (typeof elementRef.getFormatTarget === 'function') {
    // A static block's headless stand-in implements what TextStylingBar's multi-target path uses of an editor:
    // isActive, getAttributes, chain(), view.dom.blur(), isEditable, isDestroyed, schema, extensionManager, on/off
    editor = elementRef.getFormatTarget() as Editor | null
  } else if (typeof elementRef.getEditor === 'function') {
    editor = elementRef.getEditor()
  } else {
    editor = elementRef.editor ?? null
  }

  if (!editor || editor.isDestroyed) {
    return null
  }

  return editor
}

/** Collect TipTap editors for selected message elements that can receive formatting. */
export function collectMessageEditors(
  selectedElements: Element[],
  getRefForElement: (elementId: string) => ElementComponentRef | undefined | null
): Editor[] {
  const editors: Editor[] = []

  for (const element of selectedElements) {
    if (element.type !== ElementType.message) {
      continue
    }

    try {
      const componentRef = getRefForElement(element.id)
      const editor = getEditorFromElementRef(componentRef?.elementRef)
      if (editor?.isEditable) {
        editors.push(editor)
      }
    } catch {
      // Ref may be missing during teardown; skip.
    }
  }

  return editors
}

/** The text range of the first list of `listName` in the document, or null when it has none. */
function firstListRange(editor: Editor, listName: string): { from: number; to: number } | null {
  let range: { from: number; to: number } | null = null
  editor.state.doc.descendants((node, pos) => {
    if (range) return false
    if (node.type.name !== listName) return true
    range = { from: pos + 1, to: pos + node.nodeSize - 1 }
    return false
  })
  return range
}

/**
 * Turns every list of `listName` in a block back into paragraphs. Each command selects one list's items and lifts
 * them together: a whole-document selection lifts nothing, and a caret lifts one item. A nested list comes out on a
 * later pass. Works on a headless target too, whose chain starts from its stored HTML.
 */
export function liftListsOut(editor: Editor, listName: string, listItemName: string): void {
  for (let guard = 0; guard < 20; guard++) {
    const range = firstListRange(editor, listName)
    if (!range) return
    if (!editor.chain().setTextSelection(range).liftListItem(listItemName).run()) return
  }
}
